/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useRef, useState } from 'react';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import { normalizeHierarchyNodes } from '../utils/serviceRequestHierarchy';

/**
 * Loads a service request type's option hierarchy (active options only) for
 * the New/Edit Service Request pickers. Returns `levels` ordered by depth and
 * `nodes` normalized via normalizeHierarchyNodes. Empty when `typeUid` is
 * falsy, and degrades to empty on fetch failure rather than throwing.
 */
const useServiceRequestTypeHierarchy = (typeUid) => {
  const [levels, setLevels] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(!!typeUid);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLevels([]);
    setNodes([]);
    if (!typeUid) {
      setLoading(false);
      return;
    }
    const fetchHierarchy = async () => {
      try {
        setLoading(true);
        const { data } = await serviceRequestTypeService.getHierarchy(typeUid);
        if (requestId !== requestIdRef.current) return;
        setLevels(data?.levels || []);
        setNodes(normalizeHierarchyNodes(data?.nodes));
      } catch (error) {
        console.error('Failed to fetch service request type hierarchy:', error);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    };
    fetchHierarchy();
  }, [typeUid]);

  return { levels, nodes, loading };
};

export default useServiceRequestTypeHierarchy;
